import { Calendar as CalendarIcon, ChevronRight, Info, Layout, RefreshCw, Zap } from 'lucide-react';
import { useState } from 'react';
import { Calendar, CalendarLegend, EventDetailsModal } from '../../components/calendar';
import { KPIWidget } from '../../components/shared/KPIWidget';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/button';
import { CalendarEventData, useCalendarEvents } from '../../hooks/useCalendarEvents';

export function CalendarPage() {
  const { events, isLoading, error, refetch } = useCalendarEvents();
  const [selectedEvent, setSelectedEvent] = useState<CalendarEventData | null>(null);

  const handleSelectEvent = (event: CalendarEventData) => {
    setSelectedEvent(event);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <div className="space-y-8 pb-12 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#00758d] mb-1">
            <CalendarIcon size={14} />
            <span className="text-[10px] font-black uppercase tracking-widest">Planning Centralisé</span>
          </div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 uppercase">Calendrier</h1>
          <p className="text-slate-500 mt-1 font-medium text-sm">
            Suivez l'occupation de vos espaces et les événements SING
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
            className="h-11 px-5 rounded-xl border-gray-200 font-bold text-slate-600 hover:bg-gray-50 hover:text-[#00758d] transition-all"
          >
            <RefreshCw size={18} className="mr-2" />
            {isLoading ? 'Mise à jour...' : 'Actualiser'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPIWidget
          title="Réservations"
          value={String(events.filter((e) => e.type === 'booking').length)}
          subtext="Occupations confirmées"
          trend="up"
          color="primary"
          icon={<Zap />}
        />
        <KPIWidget
          title="Événements"
          value={String(events.filter((e) => e.type === 'calendar_event').length)}
          subtext="Activités planifiées"
          trend="up"
          color="secondary"
          icon={<Layout />}
        />
        <KPIWidget
          title="Total Activités"
          value={String(events.length)}
          subtext="Période actuelle"
          trend="up"
          color="success"
          icon={<CalendarIcon />}
        />
        <div className="bg-[#8E0B56]/5 border border-[#8E0B56]/10 rounded-xl p-5 flex flex-col justify-center">
          <div className="flex items-center gap-2 text-[#8E0B56] mb-1">
            <Info size={14} />
            <span className="text-[10px] font-black uppercase tracking-widest">Note Admin</span>
          </div>
          <p className="text-[11px] font-bold text-[#8E0B56] opacity-70 leading-relaxed italic">
            Cliquez sur une réservation pour consulter les détails du client et les documents associés.
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-8 items-start">
        {/* Left Column: Legend & Tips */}
        <div className="col-span-12 xl:col-span-3 space-y-6">
          <CalendarLegend />

          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight mb-4 flex items-center gap-2">
              <div className="w-1.5 h-4 bg-[#dfc32f] rounded-full"></div>
              Aide & Astuces
            </h3>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <ChevronRight size={14} className="text-[#00758d] mt-0.5 flex-shrink-0" />
                <p className="text-xs font-medium text-slate-500 leading-relaxed">
                  Survolez un événement pour voir un aperçu rapide.
                </p>
              </li>
              <li className="flex gap-3">
                <ChevronRight size={14} className="text-[#00758d] mt-0.5 flex-shrink-0" />
                <p className="text-xs font-medium text-slate-500 leading-relaxed">
                  Utilisez les boutons en haut à droite du calendrier pour changer de vue (Semaine, Jour, Agenda).
                </p>
              </li>
              <li className="flex gap-3">
                <ChevronRight size={14} className="text-[#00758d] mt-0.5 flex-shrink-0" />
                <p className="text-xs font-medium text-slate-500 leading-relaxed">
                  Les réservations en <span className="text-[#00758d] font-bold uppercase tracking-tighter">bleu</span>{' '}
                  concernent les espaces de travail.
                </p>
              </li>
            </ul>
          </div>
        </div>

        {/* Right Column: Calendar */}
        <div className="col-span-12 xl:col-span-9 bg-white border border-slate-200 rounded-[32px] p-8 shadow-sm relative overflow-hidden">
          {/* Header decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-slate-50 rounded-full -mr-32 -mt-32 opacity-50"></div>

          {/* Message d'erreur */}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm font-bold flex items-center gap-2">
              <Info size={18} />
              {error}
            </div>
          )}

          <div className="relative z-10 min-h-[700px]">
            {isLoading && events.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-50">
                <LoadingSpinner message="Synchronisation..." submessage="Chargement du calendrier" size="md" />
              </div>
            ) : null}

            <Calendar events={events} onSelectEvent={handleSelectEvent} />
          </div>
        </div>
      </div>

      {/* Modal détails événement */}
      <EventDetailsModal event={selectedEvent} onClose={handleCloseModal} />
    </div>
  );
}
